<template>
  <div class="feature-flags-tab">
    <!-- GrowthBook Status -->
    <div class="status-section">
      <div class="status-header">
        <span class="status-title">GrowthBook Status</span>
        <span class="status-badge" :class="{ ready: isReady }">
          {{ isReady ? "Ready" : "Not Ready" }}
        </span>
      </div>
    </div>

    <!-- Features List -->
    <div class="features-section">
      <div class="section-header">
        <span class="section-title">Features</span>
        <span class="count-badge">{{ featureCount }}</span>
      </div>

      <div v-if="featureCount === 0" class="empty-state">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="24" height="24">
          <path d="M3 3v18h18" />
          <path d="m19 9-5 5-4-4-3 3" />
        </svg>
        <span>No features loaded</span>
      </div>

      <div v-else class="features-list">
        <div
          v-for="feature in featuresList"
          :key="feature.key"
          class="feature-item"
          :class="{ expanded: expandedKeys.includes(feature.key) }"
        >
          <div class="feature-header" @click="toggleExpand(feature.key)">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              width="12"
              height="12"
              class="chevron"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
            <span class="feature-key" :title="feature.key">{{ feature.key }}</span>
            <span class="feature-type">{{ feature.type }}</span>

            <!-- Toggle for boolean features -->
            <button
              v-if="feature.type === 'boolean'"
              class="toggle-btn"
              :class="{ active: getOverrideValue(feature.key) ?? feature.value }"
              @click.stop="toggleFeature(feature.key, feature.value)"
              :title="hasOverride(feature.key) ? 'Override active' : 'Click to override'"
            >
              <span class="toggle-slider"></span>
            </button>

            <!-- Reset button for overridden features -->
            <button
              v-if="hasOverride(feature.key)"
              class="reset-btn"
              @click.stop="removeOverride(feature.key)"
              title="Remove override"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
              </svg>
            </button>
          </div>

          <div v-if="expandedKeys.includes(feature.key)" class="feature-value">
            <div class="value-row">
              <span class="value-label">Default:</span>
              <pre>{{ feature.displayValue }}</pre>
            </div>
            <div v-if="hasOverride(feature.key)" class="value-row override">
              <span class="value-label">Override:</span>
              <pre>{{ formatValue(getOverrideValue(feature.key)) }}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Targeting Attributes -->
    <div class="attributes-section">
      <div class="section-header">
        <span class="section-title">Targeting Attributes</span>
      </div>
      <div class="attributes-content">
        <pre>{{ attributesDisplay }}</pre>
      </div>
    </div>

    <!-- Actions -->
    <div class="actions-section">
      <button class="action-btn" @click="refreshFeatures">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12">
          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
          <path d="M3 3v5h5" />
        </svg>
        Refresh
      </button>
      <button
        class="action-btn danger"
        @click="clearAllOverrides"
        :disabled="!hasAnyOverrides"
        :class="{ disabled: !hasAnyOverrides }"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12">
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
        Clear All Overrides
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { GrowthBookService } from "@/services/GrowthBookService";

const OVERRIDES_KEY = "ai-assistant-gb-overrides";

interface FeatureItem {
  key: string;
  value: any;
  type: string;
  displayValue: string;
}

const gbService = GrowthBookService.getInstance();

const isReady = ref(false);
const features = ref<Record<string, any>>({});
const attributes = ref<Record<string, any>>({});
const overrides = ref<Record<string, any>>({});
const expandedKeys = ref<string[]>([]);

const featureCount = computed(() => Object.keys(features.value).length);

const featuresList = computed((): FeatureItem[] => {
  const items: FeatureItem[] = [];

  for (const [key, featureData] of Object.entries(features.value)) {
    // GrowthBook features have a defaultValue property
    const value = featureData?.defaultValue ?? featureData;
    const type = typeof value;
    let displayValue: string;

    if (type === "object" && value !== null) {
      displayValue = JSON.stringify(value, null, 2);
    } else {
      displayValue = String(value);
    }

    items.push({ key, value, type, displayValue });
  }

  return items.sort((a, b) => a.key.localeCompare(b.key));
});

const attributesDisplay = computed(() => {
  if (Object.keys(attributes.value).length === 0) {
    return "{}";
  }
  return JSON.stringify(attributes.value, null, 2);
});

const hasAnyOverrides = computed(() => Object.keys(overrides.value).length > 0);

const formatValue = (value: any): string => {
  if (typeof value === "object" && value !== null) {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const loadOverrides = () => {
  try {
    const stored = localStorage.getItem(OVERRIDES_KEY);
    if (stored) {
      overrides.value = JSON.parse(stored);
    }
  } catch (err) {
    console.error("[FeatureFlagsTab] Failed to load overrides", err);
    overrides.value = {};
  }
};

const saveOverrides = () => {
  try {
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides.value));
    gbService.setForcedFeatures(overrides.value);
  } catch (err) {
    console.error("[FeatureFlagsTab] Failed to save overrides", err);
  }
};

const refreshFeatures = () => {
  isReady.value = gbService.isReady();
  features.value = gbService.getAllFeatures();
  attributes.value = gbService.getAttributes();
};

const toggleExpand = (key: string) => {
  const index = expandedKeys.value.indexOf(key);
  if (index === -1) {
    expandedKeys.value.push(key);
  } else {
    expandedKeys.value.splice(index, 1);
  }
};

const toggleFeature = (key: string, currentValue: boolean) => {
  const currentOverride = overrides.value[key];
  const newValue = currentOverride !== undefined ? !currentOverride : !currentValue;

  overrides.value[key] = newValue;
  saveOverrides();
};

const hasOverride = (key: string): boolean => {
  return key in overrides.value;
};

const getOverrideValue = (key: string): any => {
  return overrides.value[key];
};

const removeOverride = (key: string) => {
  delete overrides.value[key];
  saveOverrides();
};

const clearAllOverrides = () => {
  if (confirm("Are you sure you want to clear all feature flag overrides?")) {
    overrides.value = {};
    localStorage.removeItem(OVERRIDES_KEY);
    gbService.setForcedFeatures({});
    refreshFeatures();
  }
};

onMounted(() => {
  loadOverrides();
  refreshFeatures();

  // Apply existing overrides
  if (Object.keys(overrides.value).length > 0) {
    gbService.setForcedFeatures(overrides.value);
  }
});
</script>

<style scoped>
.feature-flags-tab {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 12px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.feature-flags-tab::-webkit-scrollbar {
  width: 6px;
}

.feature-flags-tab::-webkit-scrollbar-track {
  background: transparent;
}

.feature-flags-tab::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

/* Status Section */
.status-section {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  padding: 10px 12px;
}

.status-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.status-title {
  font-size: 12px;
  font-weight: 600;
  color: #b0b0c0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.status-badge.ready {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
}

/* Section Headers */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #b0b0c0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.count-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #8090a0;
}

/* Features Section */
.features-section {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  overflow: hidden;
}

.features-list {
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.features-list::-webkit-scrollbar {
  width: 6px;
}

.features-list::-webkit-scrollbar-track {
  background: transparent;
}

.features-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.feature-item {
  border-bottom: 1px solid rgba(255, 255, 255, 0.02);
}

.feature-item:last-child {
  border-bottom: none;
}

.feature-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.feature-header:hover {
  background: rgba(255, 255, 255, 0.02);
}

.chevron {
  color: #707090;
  transition: transform 0.15s ease;
  flex-shrink: 0;
}

.feature-item.expanded .chevron {
  transform: rotate(90deg);
}

.feature-key {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #bb9af7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feature-type {
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  padding: 2px 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  color: #8090a0;
}

/* Toggle Button */
.toggle-btn {
  position: relative;
  width: 32px;
  height: 18px;
  background: rgba(128, 128, 128, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.toggle-btn.active {
  background: rgba(34, 197, 94, 0.3);
  border-color: rgba(34, 197, 94, 0.4);
}

.toggle-slider {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 14px;
  height: 14px;
  background: #808090;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.toggle-btn.active .toggle-slider {
  transform: translateX(14px);
  background: #4ade80;
}

.toggle-btn:hover {
  background: rgba(128, 128, 128, 0.4);
}

.toggle-btn.active:hover {
  background: rgba(34, 197, 94, 0.4);
}

/* Reset Button */
.reset-btn {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #f59e0b;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.reset-btn:hover {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

/* Feature Value */
.feature-value {
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.3);
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.value-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.value-row.override {
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.value-label {
  font-size: 10px;
  font-weight: 600;
  color: #707090;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.value-row.override .value-label {
  color: #f59e0b;
}

.feature-value pre {
  margin: 0;
  font-size: 11px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #7dd3fc;
  white-space: pre-wrap;
  word-break: break-all;
}

.value-row.override pre {
  color: #fbbf24;
}

/* Attributes Section */
.attributes-section {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 6px;
  overflow: hidden;
}

.attributes-content {
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.3);
}

.attributes-content pre {
  margin: 0;
  font-size: 11px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
  color: #7dd3fc;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 150px;
  overflow-y: auto;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #707090;
  padding: 32px;
}

.empty-state svg {
  opacity: 0.5;
}

.empty-state span {
  font-size: 13px;
}

/* Actions Section */
.actions-section {
  display: flex;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  margin-top: auto;
  position: sticky;
  bottom: 0;
  z-index: 1;
  background: rgba(15, 15, 26, 0.9);
  backdrop-filter: blur(6px);
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #b0b0c0;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #e0e0f0;
}

.action-btn.danger {
  border-color: rgba(239, 68, 68, 0.2);
}

.action-btn.danger:hover:not(.disabled) {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #f87171;
}

.action-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn.disabled:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #b0b0c0;
}
</style>
